import { type Map as OLMap } from 'ol';
import { type RefObject, useCallback, useState } from 'react';

export interface IUseMapExtendedViewParams {
  /** Ref all'istanza OpenLayers gestita dal widget. */
  mapRef: RefObject<OLMap | null>;
}

export interface IMapExtendedView {
  /** `true` quando la mappa è aperta nella Modal a larghezza piena. */
  isExtendedView: boolean;
  /** Alterna standard/estesa (usato dal pulsante nei controlli mappa). */
  toggle: () => void;
  /** Chiude la vista estesa. */
  close: () => void;
  /**
   * Nodo DOM stabile che ospita l'intera mappa. Viene creato una sola volta e
   * spostato (via `appendChild`) tra lo slot inline e la Modal: così l'istanza
   * OpenLayers non viene MAI rimontata né ricreata, evitando strappi grafici e
   * ri-inizializzazioni. Vedi `MapHostOutlet`.
   */
  hostNode: HTMLDivElement;
  /** Ricalcola la dimensione della mappa dopo uno spostamento o un'animazione. */
  requestMapResize: () => void;
}

/**
 * Gestisce la "visualizzazione estesa" del widget mappa. La mappa vive in un
 * nodo DOM stabile (`hostNode`) reso una sola volta via portal; per passare tra
 * inline ed estesa si sposta fisicamente quel nodo, senza rimontare React né
 * ricreare l'istanza OpenLayers. Zoom/posizione/layer/geometrie (che vivono
 * sull'istanza OL) sono quindi preservati per costruzione.
 */
export function useMapExtendedView(
  params: IUseMapExtendedViewParams,
): IMapExtendedView {
  const { mapRef } = params;

  const [isExtendedView, setIsExtendedView] = useState(false);

  const [hostNode] = useState<HTMLDivElement>(() => {
    const node = document.createElement('div');
    node.style.width = '100%';
    node.style.height = '100%';
    return node;
  });

  const toggle = useCallback(() => setIsExtendedView(current => !current), []);
  const close = useCallback(() => setIsExtendedView(false), []);

  const requestMapResize = useCallback(() => {
    requestAnimationFrame(() => mapRef.current?.updateSize());
  }, [mapRef]);

  return {
    isExtendedView,
    toggle,
    close,
    hostNode,
    requestMapResize,
  };
}
